import { RichText } from '@graphcms/rich-text-react-renderer';
import clonedeep from 'lodash.clonedeep';
import Image from 'next/image';
import Link from 'next/link';

import { LayoutWrapper } from 'components/ui/Blog/LayoutWrapper';
import PageTitle from 'components/ui/Blog/PageTitle';
import { PageSEO } from 'components/ui/SEO';
import { BLOG_QUERY, POST_PATHS_QUERY } from 'queries';
import client from 'utils/apollo';
import { formatDate } from 'utils/formatDate';
import { PostDetailType, PostPathsType } from 'utils/types';

function PageContent({
  data,
  error,
}: {
  data: { post: PostDetailType };
  error: string;
}) {
  if (error) {
    return <div>{`Failed to load ${error}`}</div>;
  }
  return (
    <LayoutWrapper>
      <PageSEO
        title={`Noxx - ${data.post.title}`}
        description={data.post.excerpt}
        coverImage={data.post.coverImage}
      />
      <div className="text-gray-900 dark:text-gray-100 pt-12 pb-8 text-lg flex flex-col">
        <Link href="/blog">&larr; Back</Link>
      </div>
      <PageTitle
        title={data.post.title}
        createdAt={formatDate(data.post.createdAt)}
      />
      <div className="flex justify-center">
        <div className="min-h-[540px] max-w-4xl">
          <RichText
            content={data.post.content.raw as any}
            renderers={{
              p: ({ children }) => <p className="py-4">{children}</p>,
              img: ({ src, altText, width, height, title }) => (
                <div className="flex flex-col items-center py-4">
                  <Image
                    src={src as string}
                    width={width}
                    height={height}
                    alt={altText || ''}
                    title={title}
                  />
                </div>
              ),
            }}
          />
          <div className=" flex flex-col items-center  space-y-8 py-8 md:py-16">
            <div className="w-96 h-0.5 bg-gray-200 dark:bg-gray-700" />
            <div className="text-gray-900 dark:text-gray-100">
              Posted by {data.post.author.name}
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
}

// taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
const deepFreeze = (object: any) => {
  const propNames = Object.getOwnPropertyNames(object);

  for (const name of propNames) {
    const value = object[name];

    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  }

  return Object.freeze(object);
};

export async function getStaticProps(context: {
  params: { slug: string };
  res: any;
}) {
  const { params } = context;
  // This value is considered fresh for ten seconds (s-maxage=10).
  // If a request is repeated within the next 10 seconds, the previously
  // cached value will still be fresh. If the request is repeated before 59 seconds,
  // the cached value will be stale but still render (stale-while-revalidate=59).
  const slug = params.slug;
  const { data, error } = await client.query({
    query: BLOG_QUERY,
    variables: {
      slug,
    },
  });

  const newData = clonedeep(data);
  return {
    props: {
      data: newData,
      error: error ? error.message : null,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 60 seconds
    // revalidate: 60, // In seconds
  };
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
  const res = await client.query({ query: POST_PATHS_QUERY });
  const { posts } = clonedeep(res.data) as PostPathsType;

  // // Get the paths we want to pre-render based on posts
  const paths = posts.map((post) => `/blog/${post.slug}`);

  // We'll pre-render only these paths at build time.
  // { fallback: 'blocking' } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: 'blocking' };
}

export default PageContent;
